import type { Locale } from '../../../../types';
import type { PluralizeTimeUnitFn, TimeUnit, TimeUnitDisplay } from '../types';

/**
 * Creates a locale-bound pluralizer for time units.
 *
 * Built on Intl.NumberFormat({ style: 'unit' }) — it renders a bare quantity
 * ("1 секунда", "5 дней") with the unit in its dictionary form.
 *
 * Intl.RelativeTimeFormat, used here before, renders a whole relative phrase and
 * puts the unit in whatever case that phrase requires — "через 1 секунду" (ru),
 * "1 аптадан" (kk), "1 秒後" (ja). Cutting the phrase down to "число + слово" left
 * those oblique forms in place, and with an open set of locales (`type Locale = string`)
 * no list of per-locale replacements can close that gap.
 *
 * Formatters are created lazily and cached per unit and `unitDisplay`
 * (`long` by default; `short`/`narrow` give CLDR abbreviations: «5 дн.», «3 ч», «2d»).
 */
const createTimeUnitPluralizer = (locale: Locale): PluralizeTimeUnitFn => {
    const formatters: Map<`${TimeUnit}:${TimeUnitDisplay}`, Intl.NumberFormat> = new Map();

    return (count, unit, options) => {
        const unitDisplay: TimeUnitDisplay = options?.unitDisplay ?? 'long';
        const cacheKey = `${unit}:${unitDisplay}` as const;
        let formatter = formatters.get(cacheKey);

        if (!formatter) {
            formatter = new Intl.NumberFormat(locale, {
                style: 'unit',
                unit,
                unitDisplay,
            });

            formatters.set(cacheKey, formatter);
        }

        return formatter.format(count);
    };
};

export { createTimeUnitPluralizer };
